import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import Summary, { Actions } from "../Summary";

/**
 * "Let's retrace our steps" — the closing screen. Submitting reveals the life
 * map PDF, generated when the actionable plan passed.
 *
 * This question's stored description is the placeholder "insert big red
 * heart-shaped button", so the copy lives here.
 */
const PROMPT =
  "Thank you for taking this journey with us. Now go and live the life you love.";

/**
 * The map is rendered by a background job fired when the previous challenge
 * passed, so it is usually ready before the learner arrives — but not always,
 * and telling someone to "check back in a moment" at the end of a twenty-one
 * lesson course is a poor way to say goodbye. Poll instead, and show it the
 * moment it lands.
 */
const POLL_INTERVAL = 3000;
const POLL_LIMIT = 40; // two minutes, then stop asking

const LifeMap: React.FC<WidgetProps> = ({
  payload,
  submit,
  submitting,
  finished,
  question,
  currentUser,
  refresh,
}) => {
  const assetUrl = payload.asset_url;
  const [gaveUp, setGaveUp] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!finished || assetUrl || !refresh || gaveUp) return;

    const timer = window.setInterval(() => {
      attempts.current += 1;

      if (attempts.current > POLL_LIMIT) {
        setGaveUp(true);
        return;
      }

      refresh();
    }, POLL_INTERVAL);

    return () => window.clearInterval(timer);
  }, [finished, assetUrl, refresh, gaveUp]);

  const community = (
    <p className="ix-hint">
      Keep in touch — you can find the alumni community on{" "}
      <a
        href="https://www.facebook.com/DesigntheLifeYouLove/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Facebook
      </a>
      , or follow Ayse on{" "}
      <a
        href="https://twitter.com/aysebirselseck"
        target="_blank"
        rel="noopener noreferrer"
      >
        Twitter
      </a>{" "}
      and{" "}
      <a
        href="https://www.instagram.com/designthelifeyoulove/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Instagram
      </a>
      .
    </p>
  );

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser} prompt={PROMPT}>
        {assetUrl && (
          <a className="dtlyl-life-map-download" href={assetUrl}>
            <div className="ix-value-lg">Download my</div>
            <div className="ix-value-xl">Life Map</div>
          </a>
        )}

        {!assetUrl && !gaveUp && (
          <div className="ix-preparing">
            <div className="ix-preparing-bar">
              <span />
            </div>
            <p className="ix-preparing-label">
              Preparing your life map, hang on a sec…
            </p>
          </div>
        )}

        {!assetUrl && gaveUp && (
          <p>
            Your life map is taking longer than usual to draw.{" "}
            <a href="#" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>
              Try again
            </a>{" "}
            in a few minutes — nothing is lost, it will be here waiting.
          </p>
        )}

        {/* Submitting this lesson completes the course. The platform's
            completion modal deliberately does not open over this screen, so the
            certificate is offered here instead. */}
        {payload.certificate_url && (
          <p className="ix-certificate-cta">
            You've finished the course —{" "}
            <Link to={payload.certificate_url}>get your certificate</Link>.
          </p>
        )}

        {community}
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} fallback={PROMPT} />
      {community}

      <Actions>
        <Button
          primary
          circular
          className="challenge-submit"
          disabled={submitting}
          loading={submitting}
          onClick={submit}
        >
          Reveal the life I love
        </Button>
      </Actions>
    </div>
  );
};

export default LifeMap;
